import { useState, useMemo, useRef, useEffect, useContext, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { PageView } from '../../components/PageView'
import { PageTopBar } from '../../components/PageTopBar'
import { Notification } from '../../components/Notification'
import { UserContext } from '../../hooks/UserContext'
import { isSuperAdminUser } from '../../utils/userUtils'
import { fetchWarehouse } from '../../services/warehouseSevices'
import { fetchC2cReceivingQueue, scanC2cRollCode, generateC2cLabel, LABEL_TIMEOUT_MS, LABEL_RETRY_TIMEOUT_MS } from '../../services/c2cReceivingServices'
import { printPdfSilently } from '../../helpers/printPdf'
import { playSoundError } from '../../configs/audio/audioNotifications'
import {
  normalizeRollCode,
  looksLikeRollCode,
  normalizeSearchTerm,
  buildLabelFileUrl,
  isForbiddenError,
  isConflictError,
  processedStorageKey,
  loadProcessed,
  pushProcessed,
  persistProcessed,
} from '../../utils/c2cReceiving'
import { resolveC2cErrorMessage } from './errorMessages'
import { OUTCOMES, PHASES, TERMINAL_SUCCESS_OUTCOMES } from './states'
import { ScanStation } from './ScanStation'
import { QueueTable } from './QueueTable'
import { ProcessedStrip } from './ProcessedStrip'
import { useNamespacedT } from './useNamespacedT'

const T = 'c2c_receiving'
const QUEUE_REFETCH_MS = 30000
// react-query's own default; kept explicit because the queue overrides `retry` (see below).
const DEFAULT_QUERY_RETRIES = 3
export const LABEL_RETRY_DELAY_MS = 3000
// A label can hold the station for two minutes, and the 409 retry can add half of that
// again. Past this the operator is offered a way out — the package is already in the strip,
// so releasing the station loses nothing.
export const CANCEL_AFTER_MS = 15000

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

// A pipeline is identified by the RAW `:warehouse_id` route string it started in (compared
// with !==) plus a generation; this is the numeric form the API payloads and the storage key
// take.
const toWarehouseId = ref => parseInt(ref, 10)

// Route: /warehouse/:warehouse_id/cubbitos, gated on c2c_receiving_read (RouterConfig).
// One scan runs arrive → label → print back to back (spec D1); the queue below is the
// recovery list for packages whose label step failed; the strip is the reprint path.
export const C2cReceivingContainer = ({ retryDelayMs = LABEL_RETRY_DELAY_MS, cancelAfterMs = CANCEL_AFTER_MS, printPdf = printPdfSilently }) => {
  const t = useNamespacedT(T)
  // `resolveC2cErrorMessage` builds absolute keys of its own, so it gets the unprefixed form.
  const translate = useNamespacedT()
  const { user } = useContext(UserContext)
  const { warehouse_id } = useParams()
  const warehouseId = toWarehouseId(warehouse_id)
  // A non-numeric :warehouse_id is not a warehouse this operator can work in: there is
  // nothing to fetch and nothing to scan into, so it takes the same no-access path as a
  // 403 instead of rendering a live station over queries that can only fail.
  const warehouseIsValid = !Number.isNaN(warehouseId)
  const queryClient = useQueryClient()
  const userId = user?.id
  // Same gate PrivateRoute uses to let a super admin onto the route in the first place: a
  // read-only station for the person who can do everything else is a support call, not a
  // safeguard (the backend authorises them too).
  const canScan = !!user?.permissions?.c2c_receiving_scan || isSuperAdminUser(user)
  // Station PCs are shared: the strip belongs to this operator AND this warehouse.
  const storageKey = processedStorageKey(userId, warehouseId)

  const scanInputRef = useRef(null)
  // One pipeline at a time; a ref, not state, so a scan that lands mid-render is dropped.
  const busyRef = useRef(false)
  // The warehouse the running pipeline belongs to: the sidebar can switch warehouses
  // without remounting this page, and a late response must not land on the new one. The
  // RAW route string on purpose — parseInt('abc') is NaN, and NaN !== NaN would make
  // every single result look stale and freeze the status line on "Recibiendo…".
  const warehouseRef = useRef(warehouse_id)
  // A pipeline outlives this component (a label can take two minutes and the sidebar can
  // navigate away): after unmount there is nothing to write to, nothing to print for, and
  // no reason to send a second request. Folded into `isStale`, so every guard the warehouse
  // switch already has covers the unmount too. The `pending` strip entry persisted before
  // the request is what makes the package recoverable when the operator comes back.
  const mountedRef = useRef(true)
  // Which run of the pipeline owns the station. Cancelar bumps it, which disowns the run in
  // flight: every guard already asks `isStale`, so the answer reaches the response, the
  // print, the 409 retry and the finally at once — without waiting for the request itself.
  const pipelineGenerationRef = useRef(0)

  const [search, setSearch] = useState('')
  const [phase, setPhase] = useState(PHASES.IDLE)
  const [statusError, setStatusError] = useState(null)
  const [lastResult, setLastResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [busyOrderId, setBusyOrderId] = useState(null)
  const [printFallback, setPrintFallback] = useState(null)
  const [processed, setProcessed] = useState(() => loadProcessed(storageKey))
  const [notification, setNotification] = useState(null)
  // Mirrors `processed` so the sessionStorage write can happen OUTSIDE the setState
  // updater: an updater must be pure (React may invoke it twice under StrictMode).
  const processedRef = useRef(processed)

  // Changing :warehouse_id does not remount the page — reload the per-warehouse strip
  // and drop the previous warehouse's card.
  useEffect(() => {
    warehouseRef.current = warehouse_id
    const stored = loadProcessed(storageKey)
    processedRef.current = stored
    setProcessed(stored)
    setLastResult(null)
    setPrintFallback(null)
    // A pipeline started in the previous warehouse still holds the scan box disabled for as
    // long as the label ceiling allows. Resetting the status line here would put "Listo para
    // escanear" over a disabled input; withBusy's finally clears status and busy together.
    if (busyRef.current) return
    setPhase(PHASES.IDLE)
    setStatusError(null)
  }, [warehouse_id, storageKey])

  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  // Disowned: the operator released the station (Cancelar) and something else may own it.
  // Stale: the result can no longer be shown — disowned, unmounted, or the warehouse changed
  // under the pipeline (the sidebar switches warehouses without remounting this page).
  const isDisowned = useCallback(pipeline => pipeline.generation !== pipelineGenerationRef.current, [])
  const isStale = useCallback(pipeline => isDisowned(pipeline) || !mountedRef.current || warehouseRef.current !== pipeline.warehouse, [isDisowned])

  const { data: warehouse } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    enabled: warehouseIsValid,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const timeZone = warehouse?.time_zone || null

  const queueParams = useMemo(() => (search ? { warehouse_id: warehouseId, search } : { warehouse_id: warehouseId }), [warehouseId, search])
  const queueQuery = useQuery(['c2c_receiving_queue', queueParams], () => fetchC2cReceivingQueue(queueParams), {
    enabled: warehouseIsValid,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    // While a 403 stands there is nothing to poll for: the operator is not a member of this
    // warehouse, so the 30s refresh only repeats the refusal for as long as the tab is open
    // (the e2e run of 2026-09-09 caught six of them). A warehouse switch changes the query
    // key, so a permitted warehouse starts polling on its own.
    refetchInterval: (data, query) => (isForbiddenError(query.state.error) ? false : QUEUE_REFETCH_MS),
    // A 403 is a permanent answer (the operator is not a member of this warehouse), and
    // retrying it left a live-looking scan box over an empty queue for 10-15s before the
    // panel appeared (e2e 2026-09-09). Everything else keeps react-query's 3 retries.
    retry: (failureCount, error) => !isForbiddenError(error) && failureCount < DEFAULT_QUERY_RETRIES,
  })
  const queueForbidden = !warehouseIsValid || (queueQuery.isError && isForbiddenError(queueQuery.error))

  // keepPreviousData is there so the table does not blink while the operator types in the
  // search box. Across a WAREHOUSE switch it would show the previous warehouse's packages
  // under the new warehouse's title — rows whose Generar guía posts to a warehouse the
  // package is not in. The ref holds the warehouse the last FRESH page belongs to, so a
  // switch renders the spinner instead of the wrong queue.
  const queueDataWarehouseRef = useRef(null)
  useEffect(() => {
    if (queueQuery.data && !queueQuery.isPreviousData) queueDataWarehouseRef.current = warehouseId
  }, [queueQuery.data, queueQuery.isPreviousData, warehouseId])
  const queueIsOtherWarehouse = queueQuery.isPreviousData && queueDataWarehouseRef.current !== warehouseId

  const scanMutation = useMutation(body => scanC2cRollCode(body))
  // The ceiling is explicit on every call: the first attempt may legitimately run past the
  // platform's 2-minute lock, the 409 retry may not (see requestLabel).
  const labelMutation = useMutation(({ orderId, warehouseId: targetWarehouseId, timeout = LABEL_TIMEOUT_MS }) =>
    generateC2cLabel(orderId, { warehouse_id: targetWarehouseId }, { timeout }),
  )
  const scanAsync = scanMutation.mutateAsync
  const labelAsync = labelMutation.mutateAsync

  // Partial-matched against the params object in the key, so it invalidates this warehouse's
  // queue whatever the search box holds.
  const invalidateQueue = useCallback(() => queryClient.invalidateQueries(['c2c_receiving_queue', { warehouse_id: warehouseId }]), [queryClient, warehouseId])

  // The only notification this screen raises is an error, so the state IS the title.
  const notifyForbidden = useCallback(() => setNotification(t('notifications.forbidden_action')), [t])

  const focusScanInput = useCallback(() => {
    const ref = scanInputRef.current
    if (!ref) return
    ref.focus()
  }, [])

  // The station is a scanner in a hand: the operator scans the first package without
  // touching the keyboard or the mouse. Once only — stealing the focus back on later
  // renders would fight whatever the operator is doing (the search box, a link).
  const focusedOnMountRef = useRef(false)
  useEffect(() => {
    if (focusedOnMountRef.current || !canScan || queueForbidden) return
    focusedOnMountRef.current = true
    focusScanInput()
  }, [canScan, queueForbidden, focusScanInput])

  // Every path that releases the station raises this flag instead of calling focusScanInput()
  // itself. React 17 batches state updates inside an event handler, so `focusScanInput()` next
  // to `setBusy(false)` in the Cancelar handler ran while the box was still `disabled={busy}`
  // in the DOM: the browser dropped the focus and `document.activeElement` fell back to
  // <body>, so the next scan went nowhere until the operator clicked the box (e2e 2026-09-09,
  // row 23). The effect below focuses AFTER the re-render that re-enables the input, which
  // makes the normal end of a pipeline deterministic too — it only worked because withBusy's
  // finally happens to run in an async continuation, which React 17 does not batch.
  const refocusRef = useRef(false)
  useEffect(() => {
    if (busy || !refocusRef.current) return
    refocusRef.current = false
    // No-op when the station is not on screen (no access, read-only, unmounted): the ref is
    // null and focusScanInput checks it.
    focusScanInput()
  }, [busy, focusScanInput])

  // Returns whether every label of this order actually reached the printer. A label with no
  // file, a fallback tab, a blocked popup and a blob that never loaded all leave the operator
  // holding a package with no paper on it — the strip has to say so rather than claim a print.
  // An EMPTY list is the same situation, not a vacuous truth: the platform answered without a
  // guía to print, so nothing came out of the printer and the badge must not say it did.
  const printLabels = useCallback(
    async (labels, pipeline) => {
      let printedEverything = labels.length > 0
      for (const label of labels) {
        // Checked before each print, not only after: nobody is watching a print dialog that
        // opens over the next warehouse — or over another screen entirely.
        if (isStale(pipeline)) return printedEverything
        const url = buildLabelFileUrl(label.label_file_url)
        if (!url) {
          printedEverything = false
          continue
        }
        // Sequential on purpose: one print dialog at a time.
        const outcome = await printPdf(url, {
          // The helper may call this long after it resolved (15s safety settle), so it is
          // a log only — the resolved value below is what the operator notice is built from.
          onFallback: error => console.warn('C2cReceiving -> print could not use the hidden iframe', error),
        })
        // The notice carries an `Abrir guía` link for THIS label; after a warehouse switch
        // it would sit next to a card that no longer exists.
        if (isStale(pipeline)) return printedEverything
        if (outcome === 'iframe') continue
        printedEverything = false
        // 'timeout' means the blob never loaded: nothing was printed AND no tab was opened,
        // so it needs the same "open it with the link" copy as a blocked popup — telling the
        // operator to look in another tab would send them hunting for one that is not there.
        if (outcome === 'fallback') setPrintFallback('fallback')
        else if (outcome !== 'skipped') setPrintFallback('blocked')
      }
      return printedEverything
    },
    [printPdf, isStale],
  )

  // 409 = another request holds the platform's 2-minute generation lock. One retry
  // after a pause covers the "double press" case; a second 409 is reported as such.
  const requestLabel = useCallback(
    async (orderId, targetWarehouseId, pipeline) => {
      try {
        return await labelAsync({ orderId, warehouseId: targetWarehouseId })
      } catch (error) {
        if (!isConflictError(error)) throw error
        await sleep(retryDelayMs)
        // The pause is long enough to leave the warehouse — or the page. Nobody is left to
        // receive the answer, so the 409 is what the caller sees (and drops as stale).
        if (isStale(pipeline)) throw error
        // A shorter ceiling for the retry: the first attempt may already have held the
        // station for two minutes, and the operator is waiting in front of the printer.
        return labelAsync({ orderId, warehouseId: targetWarehouseId, timeout: LABEL_RETRY_TIMEOUT_MS })
      }
    },
    [labelAsync, retryDelayMs, isStale],
  )

  const recordProcessed = useCallback(
    (pipeline, order, outcome, shippingLabels) => {
      const key = processedStorageKey(userId, toWarehouseId(pipeline.warehouse))
      const next = pushProcessed(processedRef.current, { order, outcome, shipping_labels: shippingLabels, at: new Date().toISOString() })
      // Both writes live outside the updater: `setProcessed` gets a plain value, so a
      // double-invoked updater can never write to sessionStorage twice. The storage write
      // happens even after unmount — that is exactly the entry the operator needs on the
      // way back — but the state write does not.
      processedRef.current = next
      if (mountedRef.current) setProcessed(next)
      persistProcessed(key, next)
    },
    [userId],
  )

  // Steps 2–5 of the pipeline (spec §6.2). Shared by the scan, the queue row action, the
  // card's retry/reprint and the strip.
  const runLabelStep = useCallback(
    async (order, { scanReplayed = false, pipeline }) => {
      setPhase(PHASES.LABELING)
      setStatusError(null)
      // The notice belongs to the package being printed: a fallback from the previous one
      // must not sit next to this card (its `Abrir guía` link points at THIS label).
      // printLabels re-sets it below if this package really does fall back.
      setPrintFallback(null)
      setBusyOrderId(order.id)
      try {
        const result = await requestLabel(order.id, toWarehouseId(pipeline.warehouse), pipeline)
        if (isStale(pipeline)) {
          console.warn('C2cReceiving -> dropping a label response the station no longer waits for', pipeline.warehouse)
          return
        }
        // No `|| []` / `|| order` fallbacks: the service validates the envelope, so a drift
        // is a throw with a name on it instead of a card built out of the REQUEST's order.
        const shippingLabels = result.shipping_labels
        const successOutcome = result.replayed ? OUTCOMES.LABEL_REPLAYED : OUTCOMES.PRINTED
        const resultOrder = result.order
        setPhase(PHASES.PRINTING)
        setLastResult({ order: resultOrder, outcome: successOutcome, scanReplayed, shipping_labels: shippingLabels, errorMessage: null })
        recordProcessed(pipeline, resultOrder, successOutcome, shippingLabels)
        // Printing can only ever be a notice: the label exists server-side from this line on.
        let printedEverything = false
        try {
          printedEverything = await printLabels(shippingLabels, pipeline)
        } catch (error) {
          console.error('C2cReceiving -> print step failed after the label was generated', error)
          // Nothing was opened, so this is the "open the label yourself" copy — telling the
          // operator to look in another tab would send them hunting for a tab that is not there.
          if (!isStale(pipeline)) setPrintFallback('blocked')
        }
        if (isStale(pipeline)) return
        // "Guía impresa" over a package with no paper on it is the one lie the operator acts
        // on: it is the badge that decides whether they press Reimprimir before shipping the
        // package on. The label exists either way — only the printing is in doubt.
        if (!printedEverything) {
          setLastResult({ order: resultOrder, outcome: OUTCOMES.LABEL_UNPRINTED, scanReplayed, shipping_labels: shippingLabels, errorMessage: null })
          recordProcessed(pipeline, resultOrder, OUTCOMES.LABEL_UNPRINTED, shippingLabels)
        }
        setPhase(PHASES.DONE)
      } catch (error) {
        if (isStale(pipeline)) {
          console.warn('C2cReceiving -> dropping a label failure the station no longer waits for', pipeline.warehouse)
          return
        }
        const message = resolveC2cErrorMessage(error, translate)
        playSoundError()
        if (isForbiddenError(error)) notifyForbidden()
        setPhase(PHASES.LABEL_FAILED)
        setStatusError(message)
        // A REPRINT that fails must not rewrite the package it was reprinting. This catch is
        // shared by all four entry points, so a 500 on Reimprimir used to replace a printed
        // entry with `label_failed` and no labels — erasing the shipping number and the
        // links the operator falls back on, and offering "Generar guía" for a package that
        // already has one. The failure is real, but it belongs in the status line.
        const existing = processedRef.current.find(item => item.order.id === order.id)
        if (existing && TERMINAL_SUCCESS_OUTCOMES.includes(existing.outcome)) {
          setLastResult({
            order: existing.order,
            outcome: existing.outcome,
            scanReplayed,
            shipping_labels: existing.shipping_labels || [],
            errorMessage: null,
          })
        } else {
          setLastResult({ order, outcome: OUTCOMES.LABEL_FAILED, scanReplayed, shipping_labels: [], errorMessage: message })
          // A client-side timeout is NOT a failed label: the platform may well have written
          // one (that is why the ceiling sits above its 2-minute lock). The card says so, and
          // the strip keeps the package on `pending`, whose action replays the label endpoint
          // and returns the existing guía — recording `label_failed` would instead invite the
          // operator to generate a second one.
          const timedOut = error?.code === 'ECONNABORTED' && !error?.response
          recordProcessed(pipeline, order, timedOut ? OUTCOMES.PENDING : OUTCOMES.LABEL_FAILED, [])
        }
      } finally {
        if (mountedRef.current) {
          // The row spinner belongs to whoever owns the station now; the queue of the
          // warehouse this pipeline ran in changed either way, so it is invalidated either way.
          if (!isDisowned(pipeline)) setBusyOrderId(null)
          invalidateQueue()
        }
      }
    },
    [requestLabel, isStale, isDisowned, recordProcessed, printLabels, translate, notifyForbidden, invalidateQueue],
  )

  const withBusy = useCallback(
    async fn => {
      if (busyRef.current) return
      // The identity of this run: the warehouse it starts in (the sidebar can switch under it)
      // and the generation that owns the station (Cancelar can take it away).
      const pipeline = { warehouse: warehouseRef.current, generation: pipelineGenerationRef.current }
      busyRef.current = true
      setBusy(true)
      try {
        await fn(pipeline)
      } catch (error) {
        // Programming errors and unexpected response shapes: never a frozen status line.
        console.error('C2cReceiving -> pipeline failed unexpectedly', error)
        if (mountedRef.current && !isDisowned(pipeline)) {
          playSoundError()
          setPhase(PHASES.SCAN_FAILED)
          setStatusError(t('errors.generic'))
        }
      } finally {
        // A disowned run releases nothing: Cancelar already cleared busy and said so, and
        // another pipeline may be holding the station by now.
        if (!isDisowned(pipeline)) {
          busyRef.current = false
          // Nothing below this line has anywhere to land once the screen is gone.
          if (mountedRef.current) {
            setBusy(false)
            // The warehouse changed under this pipeline: the switch effect deliberately left
            // the status line alone while it ran, so idle lands here — input re-enabled.
            if (warehouseRef.current !== pipeline.warehouse) {
              setPhase(PHASES.IDLE)
              setStatusError(null)
            }
            refocusRef.current = true
          }
        }
      }
    },
    [isDisowned, t],
  )

  // The escape hatch for a station frozen behind a label that will not come back (the
  // platform's lock plus the retry can add up to minutes). The package is already in the
  // strip as `pending`, so releasing the station loses nothing: the pipeline in flight is
  // disowned, its result and its print are dropped, and the operator can scan again.
  const handleCancelPipeline = useCallback(() => {
    if (!busyRef.current) return
    pipelineGenerationRef.current += 1
    busyRef.current = false
    setBusy(false)
    setBusyOrderId(null)
    setPrintFallback(null)
    setStatusError(null)
    setPhase(PHASES.CANCELLED)
    refocusRef.current = true
  }, [])

  const handleScan = useCallback(
    raw => {
      const rollCode = normalizeRollCode(raw)
      if (!rollCode) return
      // withBusy would drop this scan anyway; checked here so the format guard below cannot
      // write over the status line of a pipeline that is still running.
      if (busyRef.current) return
      // A SKU, a pick-list barcode or a carrier label scanned into this box can only come
      // back as ROLL_CODE_NOT_FOUND after a round trip that holds the station. The sticker's
      // format is known, so the answer is instant — and the operator hears it.
      if (!looksLikeRollCode(rollCode)) {
        scanInputRef.current?.resetInput()
        playSoundError()
        setPhase(PHASES.SCAN_FAILED)
        setStatusError(t('errors.INVALID_PARAMS'))
        return
      }
      withBusy(async pipeline => {
        scanInputRef.current?.resetInput()
        setPhase(PHASES.SCANNING)
        setStatusError(null)
        // Cleared here too, so a scan that fails before the label step does not keep the
        // previous package's print notice on screen either.
        setPrintFallback(null)
        let scanned
        try {
          scanned = await scanAsync({ warehouse_id: toWarehouseId(pipeline.warehouse), roll_code: rollCode })
        } catch (error) {
          if (isStale(pipeline)) {
            console.warn('C2cReceiving -> dropping a scan failure the station no longer waits for', pipeline.warehouse)
            return
          }
          playSoundError()
          if (isForbiddenError(error)) notifyForbidden()
          setPhase(PHASES.SCAN_FAILED)
          setStatusError(resolveC2cErrorMessage(error, translate))
          return
        }
        if (isStale(pipeline)) {
          console.warn('C2cReceiving -> dropping a scan response the station no longer waits for', pipeline.warehouse)
          return
        }
        // A response without an order would corrupt the strip (entries are keyed by order.id);
        // withBusy turns this into the generic red status line.
        if (!scanned?.order) throw new Error('Unexpected c2c/receiving/scan response shape')
        // Persisted BEFORE the label request so a reload in between still finds the package
        // in the strip, with the "Generar guía" action (review A7).
        recordProcessed(pipeline, scanned.order, OUTCOMES.PENDING, [])
        await runLabelStep(scanned.order, { scanReplayed: !!scanned.replayed, pipeline })
      })
    },
    [withBusy, scanAsync, isStale, notifyForbidden, translate, recordProcessed, runLabelStep, t],
  )

  // The queue rows, the card's retry/reprint and the strip all run the same label step;
  // every entry point pins the warehouse the pipeline started on. Referentially stable so
  // QueueTable's `columns` memo survives a render (it takes this as a dependency).
  const handleLabelAction = useCallback(
    order => {
      return withBusy(pipeline => {
        // Persisted BEFORE the request, like the scan path: a reload, a crash or a closed
        // tab in the ~2 minutes a label can take would otherwise lose the package entirely
        // (it leaves the queue the moment the platform ships it, and re-scanning answers
        // NOT_RECEIVABLE). An order already in the strip keeps the outcome it has until this
        // request settles — a printed package must not read as "En proceso" meanwhile.
        if (!processedRef.current.some(item => item.order.id === order.id)) {
          recordProcessed(pipeline, order, OUTCOMES.PENDING, [])
        }
        return runLabelStep(order, { pipeline })
      })
    },
    [withBusy, runLabelStep, recordProcessed],
  )

  // Referentially stable: SearchInput's debounce captures the first onChange it is given.
  const handleSearchChange = useCallback(raw => setSearch(normalizeSearchTerm(raw)), [])

  // Stable so the memoised QueueTable is not re-rendered by a fresh arrow on every keystroke
  // of the scan box. The header's Actualizar and the error strip's Reintentar are the same
  // refetch, so they are the same prop.
  const refetchQueue = queueQuery.refetch
  const handleRefresh = useCallback(() => {
    refetchQueue()
  }, [refetchQueue])

  const dismissPrintFallback = useCallback(() => setPrintFallback(null), [])

  const queueData = queueIsOtherWarehouse ? undefined : queueQuery.data
  const orders = queueData?.orders || []

  return (
    <>
      <PageView
        topMenu={
          <PageTopBar>
            <div>
              <h1 className="text-lg font-semibold">{t('title')}</h1>
              <div className="text-sm text-gray-500">{t('subtitle')}</div>
            </div>
          </PageTopBar>
        }
        childrenFullWidth={true}
        topMenuFullWidth={true}
      >
        {/* No access to the warehouse's queue means no scanning and no strip either. */}
        {!queueForbidden && (
          <>
            <ScanStation
              canScan={canScan}
              inputRef={scanInputRef}
              onScan={handleScan}
              phase={phase}
              statusError={statusError}
              lastResult={lastResult}
              timeZone={timeZone}
              busy={busy}
              onLabelAction={handleLabelAction}
              onCancel={handleCancelPipeline}
              cancelAfterMs={cancelAfterMs}
              printFallback={printFallback}
              onDismissPrintFallback={dismissPrintFallback}
            />
            <ProcessedStrip entries={processed} canScan={canScan} busy={busy} busyOrderId={busyOrderId} timeZone={timeZone} onLabelAction={handleLabelAction} />
          </>
        )}
        <QueueTable
          orders={orders}
          total={queueData?.total ?? orders.length}
          hasMore={!!queueData?.has_more}
          isLoading={queueQuery.isLoading || queueIsOtherWarehouse}
          isFetching={queueQuery.isFetching}
          isError={queueQuery.isError}
          isForbidden={queueForbidden}
          // A malformed :warehouse_id has nothing to retry — the url itself is wrong, and
          // the query never ran. A 403 does: the permission may have just landed.
          canRetryForbidden={warehouseIsValid}
          errorMessage={queueQuery.isError && !queueForbidden ? resolveC2cErrorMessage(queueQuery.error, translate) : null}
          onSearchChange={handleSearchChange}
          onRefresh={handleRefresh}
          timeZone={timeZone}
          canScan={canScan}
          onLabelAction={handleLabelAction}
          busyOrderId={busyOrderId}
          busy={busy}
        />
      </PageView>
      <Notification show={!!notification} setShow={() => setNotification(null)} type="error" title={notification || ''} ephemeral />
    </>
  )
}
